import React, { Suspense } from 'react';
import { Button } from "@/components/ui/button";
import { useSessionStore } from '@/store/sessionStore';
import { BookOpen, Clock, FileCode2, Terminal, DraftingCompass, Settings, ServerCog, Code2, Workflow, Database, Bot, UserCog, Monitor } from 'lucide-react';
import { ActiveMainPage } from '@/store/session/state';
import NavigationCard from '@/app/components/common/NavigationCard';
import { Loader2 } from 'lucide-react';

// Route-level code splitting: lazy load all page components
// so they are only loaded when the user navigates to them
const PlanInteractionPage = React.lazy(() => import('./PlanInteractionPage'));
const ContextManagementPage = React.lazy(() => import('./ContextManagementPage'));
const SessionHistoryPage = React.lazy(() => import('./SessionHistoryPage'));
const SystemPromptsManagementPage = React.lazy(() => import('./SystemPromptsManagementPage'));
const CustomSnippetsManagementPage = React.lazy(() => import('./CustomSnippetsManagementPage'));
const ContextSnippetsManagementPage = React.lazy(() => import('./ContextSnippetsManagementPage'));

const SettingsPage = React.lazy(() => import('./SettingsPage'));
const McpManagementPage = React.lazy(() => import('./McpManagementPage'));
const McpMarketplacePage = React.lazy(() => import('./McpMarketplacePage'));
const MarkdownEditorPage = React.lazy(() => import('./MarkdownEditorPage'));
const ToolHooksManagementPage = React.lazy(() => import('./ToolHooksManagementPage'));
const LlmCallLogsPage = React.lazy(() => import('./LlmCallLogsPage'));
const SubAgentsManagementPage = React.lazy(() => import('./SubAgentsManagementPage'));
const AgentsManagementPage = React.lazy(() => import('./AgentsManagementPage'));
const InstancesManagementPage = React.lazy(() => import('./InstancesManagementPage'));

const PageLoader: React.FC = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

interface ConfigLink {
  page: ActiveMainPage;
  icon: React.ReactNode;
  title: string;
}

const MainHomePageContent: React.FC<{ onNavigate: (page: ActiveMainPage) => void }> = ({ onNavigate }) => {
  const { startNewSession, isLoading } = useSessionStore();

  const handleStartNewSession = async () => {
    await startNewSession({ session_title: "New Session via Home Page" });
  };

  const workflowCards = [
    {
      page: 'history' as ActiveMainPage,
      icon: <Clock className="h-5 w-5 text-primary" />,
      title: 'Session History',
      description: 'Review and load your previous work sessions.',
      buttonText: 'View History',
    },
    {
      page: 'agents' as ActiveMainPage,
      icon: <UserCog className="h-5 w-5 text-primary" />,
      title: 'Agents',
      description: 'Configure agent presets — system prompt, templates, and model in one click.',
      buttonText: 'Manage Agents',
    },
    {
      page: 'sub_agents' as ActiveMainPage,
      icon: <Bot className="h-5 w-5 text-primary" />,
      title: 'Sub-Agents',
      description: 'Create and manage specialized sub-agents invoked by the master agent.',
      buttonText: 'Manage Sub-Agents',
    },
    {
      page: 'markdown_editor' as ActiveMainPage,
      icon: <Code2 className="h-5 w-5 text-primary" />,
      title: 'Editor',
      description: 'Browse and preview markdown and code files in the workspace.',
      buttonText: 'Open Editor',
    },
    {
      page: 'settings' as ActiveMainPage,
      icon: <Settings className="h-5 w-5 text-primary" />,
      title: 'Settings',
      description: 'Configure core workflow behaviors like execution strategy.',
      buttonText: 'Configure Settings',
    },
  ];

  const configLinks: ConfigLink[] = [
    { page: 'system_prompts', icon: <Terminal className="h-4 w-4 text-primary" />, title: 'System Prompts' },
    { page: 'context', icon: <BookOpen className="h-4 w-4 text-primary" />, title: 'Context Templates' },
    { page: 'custom_snippets', icon: <DraftingCompass className="h-4 w-4 text-primary" />, title: 'Prompt Snippets' },
    { page: 'context_snippets', icon: <FileCode2 className="h-4 w-4 text-primary" />, title: 'Context Snippets' },
    { page: 'mcp', icon: <ServerCog className="h-4 w-4 text-primary" />, title: 'MCP Servers' },
    { page: 'tool_hooks', icon: <Workflow className="h-4 w-4 text-primary" />, title: 'Tool Hooks' },
  ];

  return (
    <div className="p-4">
      <div className="text-center">
        <h1 className="text-4xl font-bold mt-4">Repoburg AI Assistant</h1>
        <p className="mt-2 text-lg text-muted-foreground">
          Your local partner for generating and modifying code.
        </p>
        <Button size="lg" className="mt-6" onClick={handleStartNewSession} disabled={isLoading}>
          {isLoading ? 'Starting Session...' : 'Start New Session'}
        </Button>
      </div>

      <div className="mt-10 w-full max-w-5xl space-y-8 text-left mx-auto">
        <section>
          <h2 className="text-3xl font-bold mb-4 text-center">Workflow</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workflowCards.map((card) => (
              <NavigationCard
                key={card.page}
                icon={card.icon}
                title={card.title}
                description={card.description}
                buttonText={card.buttonText}
                onNavigate={() => onNavigate(card.page)}
                className="hover:shadow-lg transition-shadow text-left"
              />
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-3 text-center">Configuration</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {configLinks.map((link) => (
              <button
                key={link.page}
                type="button"
                className="flex items-center gap-2 px-3 py-2 rounded-md border hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
                onClick={() => onNavigate(link.page)}
              >
                {link.icon}
                <span>{link.title}</span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-xl font-semibold mb-4 text-center">Analytics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <NavigationCard
              icon={<Database className="h-5 w-5 text-primary" />}
              title="LLM Call Logs"
              description="Inspect raw API requests and responses for debugging."
              buttonText="View Logs"
              onNavigate={() => onNavigate('llm_logs')}
              className="hover:shadow-lg transition-shadow text-left"
            />
            <NavigationCard
              icon={<Monitor className="h-5 w-5 text-primary" />}
              title="Instances"
              description="Manage Repoburg instances and view their running sessions."
              buttonText="Manage Instances"
              onNavigate={() => onNavigate('instances')}
              className="hover:shadow-lg transition-shadow text-left"
            />
          </div>
        </section>
      </div>
    </div>
  );
};


const MainHomePage: React.FC = () => {
  const { activeMainPage, setActiveMainPage } = useSessionStore();

  const handleNavigation = (page: ActiveMainPage) => {
    setActiveMainPage(page);
  };

  const renderActivePage = () => {
    switch (activeMainPage) {
      case 'plan':
        return <PlanInteractionPage />;
      case 'context':
        return <ContextManagementPage />;
      case 'system_prompts':
        return <SystemPromptsManagementPage />;
      case 'custom_snippets':
        return <CustomSnippetsManagementPage />;
      case 'context_snippets':
        return <ContextSnippetsManagementPage />;
      case 'sub_agents':
        return <SubAgentsManagementPage />;
      case 'agents':
        return <AgentsManagementPage />;
      case 'history':
        return <SessionHistoryPage onSessionLoaded={() => handleNavigation('plan')} />;
      case 'mcp':
        return <McpManagementPage />;
      case 'mcp_marketplace':
        return <McpMarketplacePage />;
      case 'settings':
        return <SettingsPage />;
      case 'markdown_editor':
        return <MarkdownEditorPage />;
      case 'tool_hooks':
        return <ToolHooksManagementPage />;
      case 'llm_logs':
        return <LlmCallLogsPage />;
      case 'instances':
        return <InstancesManagementPage />;
      case 'home':
      default:
        return (
          <MainHomePageContent onNavigate={handleNavigation} />
        );
    }
  };

  return (
    <Suspense fallback={<PageLoader />}>
      {renderActivePage()}
      {activeMainPage !== 'home' && activeMainPage !== 'instances' && (
        <Button
          variant="outline"
          size="icon"
          className="fixed bottom-4 left-4 z-50 h-10 w-10 rounded-full shadow-lg"
          onClick={() => handleNavigation('instances')}
          title="Manage Instances"
        >
          <Monitor className="h-5 w-5" />
        </Button>
      )}
    </Suspense>
  );
};

export default MainHomePage;